import React, { useState, useCallback, forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import '../styles/ImportBuyer.css';
import * as XLSX from 'xlsx';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, onSnapshot, deleteDoc, collection, getDocs } from 'firebase/firestore';
import { createBuyer, findDuplicateBuyer } from '../functions';
import { Checkbox } from '../components';
import { HugeiconsIcon } from '@hugeicons/react';
import { FileUploadIcon, Cancel01Icon } from '@hugeicons/core-free-icons';
import { colour } from '../styles/GlobalStyles';

const STEPS = { UPLOAD: 'upload', PROCESSING: 'processing', REVIEW: 'review', IMPORTING: 'importing', DONE: 'done' };

const ImportBuyerModal = forwardRef(function ImportBuyerModal(
    { companyId, existingBuyers, onComplete, onValidityChange, onStepChange },
    ref
) {
    const [step, setStep] = useState(STEPS.UPLOAD);
    const [file, setFile] = useState(null);
    const [parsedData, setParsedData] = useState(null); // { headers, rows }
    const [processedResult, setProcessedResult] = useState(null);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [error, setError] = useState(null);
    const [importProgress, setImportProgress] = useState({ done: 0, total: 0, failed: 0 });
    const [dragActive, setDragActive] = useState(false);
    const [processingStatus, setProcessingStatus] = useState('Mapping your data with AI...');
    const fileInputRef = useRef(null);
    const isImportingRef = useRef(false);
    const importIdRef = useRef(null);
    const unsubscribeRef = useRef(null);

    // Cleanup Firestore listener on unmount
    useEffect(() => {
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, []);

    // Notify parent of validity changes
    const updateValidity = useCallback((isValid, actionText) => {
        if (onValidityChange) onValidityChange(isValid);
        if (onStepChange) onStepChange(actionText);
    }, [onValidityChange, onStepChange]);

    // Update step and validity together
    const goToStep = useCallback((newStep, result) => {
        setStep(newStep);
        switch (newStep) {
            case STEPS.UPLOAD:
                updateValidity(false, 'Import Buyers');
                break;
            case STEPS.PROCESSING:
                updateValidity(false, 'Processing...');
                break;
            case STEPS.REVIEW: {
                const count = result?.buyers?.length || 0;
                updateValidity(count > 0, `Import ${count} Buyer${count !== 1 ? 's' : ''}`);
                break;
            }
            case STEPS.IMPORTING:
                updateValidity(false, 'Importing...');
                break;
            case STEPS.DONE:
                updateValidity(true, 'Close');
                break;
            default:
                break;
        }
    }, [updateValidity]);

    // ──── File parsing ────
    const parseFile = useCallback((fileObj) => {
        setError(null);

        // Client-side file size limit (5MB)
        if (fileObj.size > 5 * 1024 * 1024) {
            setError('File is too large (max 5MB). Please reduce the number of rows or columns and try again.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

                if (!jsonData || jsonData.length === 0) {
                    setError('The file appears to be empty. Please check your spreadsheet.');
                    return;
                }

                const headers = Object.keys(jsonData[0]);
                if (headers.length === 0) {
                    setError('No columns found in the spreadsheet.');
                    return;
                }

                if (headers.length > 100) {
                    setError('Too many columns (max 100). Please simplify your spreadsheet.');
                    return;
                }

                if (jsonData.length > 1000) {
                    setError(`Too many rows (${jsonData.length}). Please limit to 1,000 buyers per import.`);
                    return;
                }

                setFile(fileObj);
                setParsedData({ headers, rows: jsonData });

                // Auto-advance to processing
                processWithAI(headers, jsonData);
            } catch (err) {
                console.error('Error parsing file:', err);
                setError('Could not parse this file. Please ensure it is a valid .csv, .xlsx, or .xls file.');
            }
        };
        reader.onerror = () => {
            setError('Failed to read file. Please try again.');
        };
        reader.readAsArrayBuffer(fileObj);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ──── Helper: handle completed import data (from Firestore or httpsCallable) ────
    const handleCompleteResult = useCallback((data) => {
        const buyersArr = data.buyers || [];
        const safeResult = { ...data, buyers: buyersArr };
        setProcessedResult(safeResult);
        setSelectedRows(new Set(buyersArr.map((_, i) => i)));
        goToStep(STEPS.REVIEW, safeResult);
    }, [goToStep]);

    // ──── AI Processing ────
    const processWithAI = useCallback(async (headers, rows) => {
        goToStep(STEPS.PROCESSING);
        setError(null);
        setProcessingStatus('Mapping your data with AI...');

        // Generate unique import ID and start Firestore listener BEFORE calling the function
        const importId = `import_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        importIdRef.current = importId;

        const firestoreDb = getFirestore();
        const importDocRef = doc(firestoreDb, 'companies', companyId, 'buyerImports', importId);
        let resolvedViaFirestore = false;

        // Clean up any existing listener
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
        }

        unsubscribeRef.current = onSnapshot(importDocRef, (snapshot) => {
            const snapData = snapshot.data();
            if (!snapData) return;

            // Update progress text in real-time
            if (snapData.progress) {
                setProcessingStatus(snapData.progress);
            }

            if (snapData.status === 'complete' && !resolvedViaFirestore) {
                resolvedViaFirestore = true;
                if (unsubscribeRef.current) {
                    unsubscribeRef.current();
                    unsubscribeRef.current = null;
                }
                // Strip Firestore metadata fields before passing to handler
                const { status: _s, progress: _p, createdAt: _c, updatedAt: _u, buyerChunkCount, ...resultMeta } = snapData;

                // Read buyer chunks from subcollection
                (async () => {
                    try {
                        if (buyerChunkCount > 0) {
                            const chunksSnap = await getDocs(
                                collection(firestoreDb, 'companies', companyId, 'buyerImports', importId, 'buyerChunks')
                            );
                            const allBuyers = [];
                            chunksSnap.docs
                                .sort((a, b) => a.data().index - b.data().index)
                                .forEach(d => allBuyers.push(...d.data().buyers));
                            handleCompleteResult({ ...resultMeta, buyers: allBuyers });
                        } else {
                            handleCompleteResult(resultMeta);
                        }
                    } catch (e) {
                        console.error('Failed to read buyer chunks:', e);
                        setError('Failed to load processed buyers. Please try again.');
                        setFile(null);
                        setParsedData(null);
                        goToStep(STEPS.UPLOAD);
                    }
                })();
            }

            if (snapData.status === 'error') {
                if (unsubscribeRef.current) {
                    unsubscribeRef.current();
                    unsubscribeRef.current = null;
                }
                setError(snapData.error || 'An error occurred while processing.');
                setFile(null);
                setParsedData(null);
                goToStep(STEPS.UPLOAD);
            }
        }, (listenError) => {
            // Firestore listener failed — httpsCallable response will serve as fallback
            console.warn('Firestore import listener error:', listenError.message);
            unsubscribeRef.current = null;
        });

        // Call the cloud function — data delivery happens via Firestore listener
        try {
            const functions = getFunctions();
            const processBuyerImportFn = httpsCallable(functions, 'processBuyerImport', { timeout: 1200000 });
            const response = await processBuyerImportFn({
                importId,
                headers,
                allRows: rows.slice(0, 1000)
            });

            // If Firestore listener already handled it, skip
            if (resolvedViaFirestore) return;

            const result = response.data;
            if (result.status !== 200 || !result.data) {
                if (unsubscribeRef.current) {
                    unsubscribeRef.current();
                    unsubscribeRef.current = null;
                }
                setError(result.error || 'Failed to process the file. Please try again.');
                setFile(null);
                setParsedData(null);
                goToStep(STEPS.UPLOAD);
                return;
            }

            // Use httpsCallable response if Firestore hasn't delivered yet
            if (!resolvedViaFirestore) {
                if (unsubscribeRef.current) {
                    unsubscribeRef.current();
                    unsubscribeRef.current = null;
                }
                handleCompleteResult(result.data);
            }
        } catch (err) {
            // If deadline-exceeded, the function is still running — Firestore listener will pick up results
            if (err.code === 'deadline-exceeded' || err.message?.includes('deadline-exceeded')) {
                console.log('Client timeout — waiting for results via Firestore listener...');
                setProcessingStatus('Still processing — almost done...');
                return; // Let onSnapshot handle it
            }

            // Actual error — clean up listener
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
            console.error('Error processing buyer import:', err);
            setError(err.message || 'An error occurred while processing. Please try again.');
            setFile(null);
            setParsedData(null);
            goToStep(STEPS.UPLOAD);
        }
    }, [goToStep, companyId, handleCompleteResult]);

    // ──── Cleanup: delete the temporary import doc from Firestore ────
    const cleanupImportDoc = useCallback(async () => {
        if (!importIdRef.current || !companyId) return;
        try {
            const firestoreDb = getFirestore();
            const importDocRef = doc(firestoreDb, 'companies', companyId, 'buyerImports', importIdRef.current);

            // Delete buyer chunks subcollection first (Firestore doesn't cascade deletes)
            const chunksSnap = await getDocs(collection(importDocRef, 'buyerChunks'));
            if (!chunksSnap.empty) {
                await Promise.all(chunksSnap.docs.map(d => deleteDoc(d.ref)));
            }

            // Then delete main doc
            await deleteDoc(importDocRef);
        } catch (e) {
            console.warn('Failed to clean up import doc:', e.message);
        }
        importIdRef.current = null;
    }, [companyId]);

    // ──── Import (submit) ────
    const handleSubmit = useCallback(async () => {
        if (isImportingRef.current) return;
        if (!processedResult || !companyId || selectedRows.size === 0) return;
        setError(null);

        const buyersToImport = processedResult.buyers.filter((_, i) => selectedRows.has(i));

        // Pre-check for duplicates against existing buyers and within the batch
        const duplicateIndices = [];
        const duplicateMessages = [];
        const seenEmails = new Map();
        const seenPhones = new Map();

        for (let i = 0; i < buyersToImport.length; i++) {
            const contact = buyersToImport[i].contact || {};
            const buyerName = contact.name || `Row ${i + 1}`;
            const normEmail = contact.email?.trim().toLowerCase() || null;
            const rawPhone = contact.phone ? ('' + contact.phone).replace(/\D/g, '') : null;
            const normPhone = rawPhone && rawPhone.length >= 10 ? rawPhone : null;

            // Check against existing buyers in the company
            const dupResult = findDuplicateBuyer(existingBuyers || [], contact);
            if (dupResult.isDuplicate) {
                const fieldLabel = dupResult.matchedField === 'email' ? 'email' : 'phone';
                duplicateIndices.push(i);
                duplicateMessages.push(`"${buyerName}" — duplicate ${fieldLabel} (matches existing buyer "${dupResult.matchedBuyerName}")`);
                continue;
            }

            // Check within the import batch itself
            if (normEmail && seenEmails.has(normEmail)) {
                duplicateIndices.push(i);
                duplicateMessages.push(`"${buyerName}" — duplicate email within import`);
                continue;
            }
            if (normPhone && seenPhones.has(normPhone)) {
                duplicateIndices.push(i);
                duplicateMessages.push(`"${buyerName}" — duplicate phone within import`);
                continue;
            }

            if (normEmail) seenEmails.set(normEmail, i);
            if (normPhone) seenPhones.set(normPhone, i);
        }

        if (duplicateIndices.length > 0) {
            // Find the original indices in processedResult.buyers and deselect them
            const originalIndices = duplicateIndices.map(di => {
                const buyer = buyersToImport[di];
                return processedResult.buyers.indexOf(buyer);
            }).filter(idx => idx !== -1);

            setSelectedRows(prev => {
                const next = new Set(prev);
                for (const idx of originalIndices) next.delete(idx);
                if (onValidityChange) onValidityChange(next.size > 0);
                return next;
            });

            const summary = duplicateMessages.length <= 5
                ? duplicateMessages.join('\n')
                : duplicateMessages.slice(0, 5).join('\n') + `\n...and ${duplicateMessages.length - 5} more`;
            setError(`${duplicateIndices.length} duplicate(s) found and deselected:\n${summary}`);
            return;
        }

        isImportingRef.current = true;
        goToStep(STEPS.IMPORTING);
        setImportProgress({ done: 0, total: buyersToImport.length, failed: 0 });

        let successCount = 0;
        let failCount = 0;

        for (const buyer of buyersToImport) {
            // Strip internal fields
            const { _rowIndex, _warnings, _countyNames, ...buyerData } = buyer;
            try {
                const result = await createBuyer(companyId, buyerData);
                if (result.status === 201) {
                    successCount++;
                } else {
                    failCount++;
                }
            } catch {
                failCount++;
            }
            setImportProgress({ done: successCount + failCount, total: buyersToImport.length, failed: failCount });
        }

        setImportProgress({ done: successCount + failCount, total: buyersToImport.length, failed: failCount });
        isImportingRef.current = false;
        goToStep(STEPS.DONE);

        // Clean up the temporary Firestore import doc
        cleanupImportDoc();
    }, [processedResult, companyId, selectedRows, existingBuyers, onValidityChange, goToStep, cleanupImportDoc]);

    useImperativeHandle(ref, () => ({
        submit: () => {
            if (step === STEPS.DONE) {
                if (onComplete) onComplete(importProgress);
            } else {
                handleSubmit();
            }
        }
    }), [step, handleSubmit, onComplete, importProgress]);

    // ──── Selection helpers ────
    const toggleRow = useCallback((index) => {
        setSelectedRows(prev => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            // Update validity
            if (onValidityChange) onValidityChange(next.size > 0);
            if (onStepChange) onStepChange(`Import ${next.size} Buyer${next.size !== 1 ? 's' : ''}`);
            return next;
        });
    }, [onValidityChange, onStepChange]);

    const toggleAll = useCallback(() => {
        if (!processedResult) return;
        const allCount = processedResult.buyers.length;
        if (selectedRows.size === allCount) {
            setSelectedRows(new Set());
            if (onValidityChange) onValidityChange(false);
            if (onStepChange) onStepChange('Import 0 Buyers');
        } else {
            setSelectedRows(new Set(processedResult.buyers.map((_, i) => i)));
            if (onValidityChange) onValidityChange(true);
            if (onStepChange) onStepChange(`Import ${allCount} Buyer${allCount !== 1 ? 's' : ''}`);
        }
    }, [processedResult, selectedRows, onValidityChange, onStepChange]);

    // ──── Drag & drop handlers ────
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const droppedFile = e.dataTransfer.files?.[0];
        if (droppedFile) {
            const ext = droppedFile.name.split('.').pop().toLowerCase();
            if (['csv', 'xlsx', 'xls'].includes(ext)) {
                parseFile(droppedFile);
            } else {
                setError('Please upload a .csv, .xlsx, or .xls file.');
            }
        }
    };

    const handleFileInput = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            parseFile(selectedFile);
        }
    };

    const clearFile = () => {
        setFile(null);
        setParsedData(null);
        setProcessedResult(null);
        setSelectedRows(new Set());
        setError(null);
        goToStep(STEPS.UPLOAD);
        if (fileInputRef.current) fileInputRef.current.value = '';
        // Clean up listener and import doc
        if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
        }
        cleanupImportDoc();
    };

    // ──── Geography summary helper ────
    const getGeoSummary = (buyer) => {
        const geo = buyer?.geography;
        if (!geo) return '-';
        const parts = [];
        if (geo.states?.length > 0) parts.push(geo.states.join(', '));
        if (geo.counties?.length > 0) {
            const names = buyer._countyNames || [];
            if (names.length > 0) {
                parts.push(names.join(', '));
            } else {
                parts.push(`${geo.counties.length} count${geo.counties.length > 1 ? 'ies' : 'y'}`);
            }
        }
        if (geo.zipCodes?.length > 0) parts.push(`${geo.zipCodes.length} ZIP${geo.zipCodes.length > 1 ? 's' : ''}`);
        return parts.length > 0 ? parts.join(' | ') : '-';
    };

    // ──── Render: Upload step ────
    if (step === STEPS.UPLOAD) {
        return (
            <div className="import-buyer-modal-container">
                {error && (
                    <span className="body-regular colour-error" style={{ whiteSpace: 'pre-line' }}>{error}</span>
                )}
                {!file ? (
                    <div
                        className={`import-dropzone ${dragActive ? 'import-dropzone-active' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <div className="import-dropzone-icon">
                            <HugeiconsIcon icon={FileUploadIcon} color={colour.gray04} size={32} />
                        </div>
                        <div className="import-dropzone-text">
                            <span className="body-semibold colour-primary">
                                Drop your file here or click to browse
                            </span>
                            <span className="body-regular colour-secondary">
                                Supports .csv, .xlsx, and .xls files
                            </span>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,.xls"
                            onChange={handleFileInput}
                            style={{ display: 'none' }}
                        />
                    </div>
                ) : (
                    <div className="import-file-selected">
                        <div className="import-file-info">
                            <span className="body-semibold colour-primary">{file.name}</span>
                            <span className="body-regular colour-secondary">
                                {parsedData ? `${parsedData.rows.length} rows, ${parsedData.headers.length} columns` : 'Parsing...'}
                            </span>
                        </div>
                        <div className="import-file-remove" onClick={clearFile}>
                            <HugeiconsIcon icon={Cancel01Icon} color={colour.gray05} size={16} />
                        </div>
                    </div>
                )}
                <div className="import-guidelines">
                    <span className="body-semibold colour-primary">Tips for best results</span>
                    <ul className="body-regular colour-secondary">
                        <li>Include a column for buyer names (required)</li>
                        <li>Use separate columns for different data types (e.g. "City" and "State" rather than one "Location" column)</li>
                        <li>ZIP codes should be 5-digit format</li>
                        <li>For multiple values in one cell, separate with commas</li>
                    </ul>
                </div>
            </div>
        );
    }

    // ──── Render: Processing step ────
    if (step === STEPS.PROCESSING) {
        return (
            <div className="import-buyer-modal-container">
                <div className="import-processing">
                    <div className="import-processing-spinner" />
                    <span className="body-semibold colour-primary">{processingStatus}</span>
                    <span className="body-regular colour-secondary">
                        This may take 5-10 minutes for large files
                    </span>
                </div>
            </div>
        );
    }

    // ──── Render: Importing step ────
    if (step === STEPS.IMPORTING) {
        const pct = importProgress.total > 0 ? Math.round((importProgress.done / importProgress.total) * 100) : 0;
        return (
            <div className="import-buyer-modal-container">
                <div className="import-progress">
                    <span className="body-semibold colour-primary">
                        Importing buyers... {importProgress.done} / {importProgress.total}
                    </span>
                    <div className="import-progress-bar-container">
                        <div className="import-progress-bar" style={{ width: `${pct}%` }} />
                    </div>
                    {importProgress.failed > 0 && (
                        <span className="body-regular colour-error">
                            {importProgress.failed} failed
                        </span>
                    )}
                </div>
            </div>
        );
    }

    // ──── Render: Done step ────
    if (step === STEPS.DONE) {
        return (
            <div className="import-buyer-modal-container">
                <div className="import-progress">
                    <span className="body-semibold colour-primary">
                        Import complete
                    </span>
                    <span className="body-regular colour-secondary">
                        {importProgress.done - importProgress.failed} buyer{importProgress.done - importProgress.failed !== 1 ? 's' : ''} imported successfully
                        {importProgress.failed > 0 ? `, ${importProgress.failed} failed` : ''}
                    </span>
                </div>
            </div>
        );
    }

    // ──── Render: Review step ────
    if (step === STEPS.REVIEW && processedResult) {
        const { buyers, unmappedColumns, skippedRows, warningRows } = processedResult;

        return (
            <div className="import-buyer-modal-container">
                {error && (
                    <span className="body-regular colour-error" style={{ whiteSpace: 'pre-line' }}>{error}</span>
                )}

                {/* Summary */}
                <div className="import-review-summary">
                    <div className="import-review-stat">
                        <span className="body-semibold colour-primary">{buyers.length}</span>
                        <span className="body-regular colour-secondary">Ready to import</span>
                    </div>
                    {warningRows > 0 && (
                        <div className="import-review-stat">
                            <span className="body-semibold colour-primary">{warningRows}</span>
                            <span className="body-regular colour-secondary">With warnings</span>
                        </div>
                    )}
                    {skippedRows > 0 && (
                        <div className="import-review-stat">
                            <span className="body-semibold colour-primary">{skippedRows}</span>
                            <span className="body-regular colour-secondary">Skipped (no name)</span>
                        </div>
                    )}
                </div>

                {/* Unmapped columns */}
                {unmappedColumns && unmappedColumns.length > 0 && (
                    <div className="import-unmapped-section">
                        <span className="body-semibold colour-secondary">
                            Columns we couldn't map ({unmappedColumns.length})
                        </span>
                        <div className="import-unmapped-chips">
                            {unmappedColumns.map(col => (
                                <span key={col} className="import-unmapped-chip body-regular colour-secondary">
                                    {col}
                                </span>
                            ))}
                        </div>
                        <span className="body-regular colour-tertiary">
                            These columns were skipped. You can manually update buyer details after import.
                        </span>
                    </div>
                )}

                {/* Select all */}
                <div className="import-select-all-row">
                    <div onClick={toggleAll} style={{ cursor: 'pointer' }}>
                        <Checkbox
                            label={`Select all (${buyers.length})`}
                            selected={selectedRows.size === buyers.length}
                            disabled={false}
                        />
                    </div>
                    <span className="body-regular colour-secondary">
                        {selectedRows.size} selected
                    </span>
                </div>

                {/* Buyer preview table */}
                <div className="import-review-table-container">
                    <table className="import-review-table">
                        <thead>
                            <tr>
                                <th className="import-checkbox-cell body-regular colour-secondary"></th>
                                <th className="body-regular colour-secondary">#</th>
                                <th className="body-regular colour-secondary">Name</th>
                                <th className="body-regular colour-secondary">Email</th>
                                <th className="body-regular colour-secondary">Phone</th>
                                <th className="body-regular colour-secondary">Geography</th>
                                <th className="body-regular colour-secondary">Warnings</th>
                            </tr>
                        </thead>
                        <tbody>
                            {buyers.map((buyer, idx) => (
                                <tr key={idx}>
                                    <td
                                        className="import-checkbox-cell"
                                        onClick={() => toggleRow(idx)}
                                    >
                                        <Checkbox
                                            label={null}
                                            selected={selectedRows.has(idx)}
                                            disabled={false}
                                        />
                                    </td>
                                    <td className="body-regular colour-secondary">{idx + 1}</td>
                                    <td className="body-regular colour-primary">{buyer.contact?.name || '-'}</td>
                                    <td className="body-regular colour-secondary">{buyer.contact?.email || '-'}</td>
                                    <td className="body-regular colour-secondary">{buyer.contact?.phone || '-'}</td>
                                    <td className="body-regular colour-secondary">{getGeoSummary(buyer)}</td>
                                    <td>
                                        {buyer._warnings?.length > 0 ? (
                                            <div className="import-warning-cell">
                                                <div className="import-warning-dot" />
                                                <span className="body-regular import-warning-text">
                                                    {buyer._warnings.join('; ')}
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="body-regular colour-secondary">-</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return null;
});

export default ImportBuyerModal;
